import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from '../Icon';
import { Avatar } from '../Avatar';
import { statusBadgeClass } from '../../lib/access';
import { formatDateTime } from '../../lib/format';
import {
  relTime,
  requestChips,
  rowPeople,
  systemKind,
  type Chip,
  type ListRow,
} from '../../lib/requestChips';

/**
 * One dense, two-line row of the /access list: status icon · "<System> access ·
 * name" + who/when + label chips · comments, assignees and last update.
 */
export const STATUS_ICON: Record<string, { icon: IconName; mod: string; label: string }> = {
  pending: { icon: 'clock', mod: '', label: 'Pending' },
  approved: { icon: 'check', mod: 'req-row__icon--ok', label: 'Approved' },
  denied: { icon: 'cross', mod: 'req-row__icon--off', label: 'Denied' },
  cancelled: { icon: 'cross', mod: '', label: 'Cancelled' },
};

const AV_TINTS = ['', 'req-av--v', 'req-av--g', 'req-av--a'];
/** Stable tint per person, so the same assignee keeps one colour across rows. */
function tint(email: string): string {
  let h = 0;
  for (const c of email) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AV_TINTS[h % AV_TINTS.length];
}

export function ChipView({ c }: { c: Chip }) {
  const icon = c.icon ? <Icon name={c.icon} size={13} className="req-ico" /> : null;
  switch (c.tone) {
    case 'status':
      return <span className={statusBadgeClass(c.variant ?? '')}>{c.label}</span>;
    case 'risk':
      return (
        <span className={`risk-badge risk-badge--${c.variant}`} title={c.title}>
          {c.label}
        </span>
      );
    case 'tag':
      return <span className="tag-badge">{c.label}</span>;
    case 'plain':
      return (
        <span className="req-chip" title={c.title}>
          {icon}
          {c.label}
        </span>
      );
    default:
      return (
        <span className={`req-chip req-chip--${c.tone}`} title={c.title}>
          {icon}
          {c.label}
        </span>
      );
  }
}

export interface RowFrameProps {
  testid: string;
  id: string;
  /** Set as `data-kind` (unified list). */
  dataKind?: string;
  mine: boolean;
  status: { icon: IconName; mod: string; label: string };
  href: string;
  titleAttr?: string;
  title: ReactNode;
  meta: ReactNode;
  chips: ReactNode;
  commentCount?: number;
  people: string[];
  updatedAt: string;
  now: number;
}

/**
 * The row's layout without any access-request knowledge: status icon · title link +
 * meta line + chips · comments, avatars and last update. `RequestRow` renders the
 * /access row through it; the /tasks list passes its own content.
 */
export function RequestRowFrame(p: RowFrameProps) {
  return (
    <li
      className={p.mine ? 'req-row req-row--mine' : 'req-row'}
      data-testid={p.testid}
      data-id={p.id}
      data-kind={p.dataKind}
    >
      <span className={`req-row__icon ${p.status.mod}`} title={p.status.label}>
        <Icon name={p.status.icon} className="req-ico" />
      </span>
      <div>
        <Link to={p.href} className="req-row__title" title={p.titleAttr}>
          {p.title}
        </Link>
        <div className="req-row__meta">{p.meta}</div>
        <div className="req-chips">{p.chips}</div>
      </div>
      <div className="req-row__side">
        <span className="req-row__stats">
          {(p.commentCount ?? 0) > 0 && (
            <span
              className="req-row__stat"
              title={`${p.commentCount} comment${p.commentCount === 1 ? '' : 's'}`}
            >
              <Icon name="quote" size={13} className="req-ico" /> {p.commentCount}
            </span>
          )}
          {p.people.length > 0 && (
            // Up to three faces; the rest fold into one "+N" disc whose tooltip names them.
            <span className="req-avstack">
              {p.people.slice(0, 3).map((x) => (
                <Avatar key={x} email={x} className={`req-av ${tint(x)}`} title={x} ariaHidden={false} />
              ))}
              {p.people.length > 3 && (
                <span className="req-av" title={p.people.slice(3).join(', ')}>
                  +{p.people.length - 3}
                </span>
              )}
            </span>
          )}
        </span>
        <span title={formatDateTime(p.updatedAt)}>updated {relTime(p.updatedAt, p.now)}</span>
      </div>
    </li>
  );
}

/** The meta line of an access row (also used by the unified list). */
export function accessMeta(r: ListRow, me: string, now: number, idLabel = `#${r.id}`): ReactNode {
  const mine = !!me && r.requesterEmail.toLowerCase() === me.toLowerCase();
  return (
    <>
      {idLabel} · created{' '}
      <span title={formatDateTime(r.createdAt)}>{relTime(r.createdAt, now)}</span> by{' '}
      <strong title={r.requesterEmail}>{mine ? 'you' : r.requesterEmail.split('@')[0]}</strong>
      {mine && r.status === 'cancelled' && ' · withdrawn by you'}
    </>
  );
}

export function RequestRow({ r, me, now }: { r: ListRow; me: string; now: number }) {
  const mine = !!me && r.requesterEmail.toLowerCase() === me.toLowerCase();
  return (
    <RequestRowFrame
      testid="req-row"
      id={r.id}
      mine={mine}
      status={STATUS_ICON[r.status] ?? STATUS_ICON.pending}
      href={`/tasks/${r.id}`}
      titleAttr={`${r.resourceName} (${r.resourceType})`}
      title={
        <>
          <span className="req-row__kind">{systemKind(r.resourceType)} ·</span> {r.resourceName}
        </>
      }
      meta={accessMeta(r, me, now)}
      chips={requestChips(r, me, now).map((c, i) => (
        <ChipView key={i} c={c} />
      ))}
      commentCount={r.commentCount}
      people={rowPeople(r)}
      updatedAt={r.updatedAt ?? r.createdAt}
      now={now}
    />
  );
}
